import React from 'react';

import * as R from 'ramda';
import { isNil } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import {
  checkGenerationStatus,
  getGenerationResult,
  PageGenerationResult,
  rateGeneration,
  retryGeneration,
  startGeneration,
} from 'api/pageGenerations';
import mockData from 'components/Pages/Admin/PageTypes/Property/Landing/mockData';
import { PAGE_TYPE_PROPERTY } from 'constants/index';
import { useAIGeneration } from 'hooks/useAIGeneration';
import { showErrorSnackbar } from 'hooks/useSnackbar';
import { setLandingData } from 'store/page';
import { landingDataSelector, pageDataSelector } from 'store/page/selectors';
import { setAppHasChangesAction } from 'store/unsavedChanges';

import * as AICardTypes from '../AIActions/AICard/AICard.types';
import { getNewLandingData } from './utils';

type EmptyContext = {
  cardState: 'idle';
  isShowAIActions: false;
  onCardClear: () => null;
  onCardClose: () => null;
  onCardCancelGeneration: () => null;
  onCardGenerate: () => null;
  onCardRetry: () => null;
  onCardRate: () => null;
};
type NotEmptyContext = {
  cardState: AICardTypes.AICardState;
  isShowAIActions: boolean;
  onCardClear: () => Promise<void>;
  onCardClose: () => void;
  onCardCancelGeneration: () => void;
  onCardGenerate: () => Promise<void>;
  onCardRetry: () => Promise<any>;
  onCardRate: (rate: 'positive' | 'negative') => Promise<any>;
};

type Context = EmptyContext | NotEmptyContext;

const PageGenerationContext = React.createContext<Context>({
  cardState: 'idle',
  isShowAIActions: false,
  onCardClear: () => null,
  onCardClose: () => null,
  onCardCancelGeneration: () => null,
  onCardGenerate: () => null,
  onCardRetry: () => null,
  onCardRate: () => null,
});

export const PageGenerationProvider = ({
  children,
  initialShowAIActions,
}: {
  children: React.ReactNode;
  initialShowAIActions?: boolean;
}) => {
  const dispatch = useDispatch();
  const landingData = useSelector(landingDataSelector);
  const page = useSelector(pageDataSelector);
  const [isShowAIActions, setIsShowAIActions] = React.useState(() => {
    if (!isNil(initialShowAIActions)) return initialShowAIActions;
    return page.currentStep === 'rooms' && page.type === PAGE_TYPE_PROPERTY;
  });

  const [cardState, setCardState] = React.useState<AICardTypes.AICardState>('idle');
  const aiGeneration = useAIGeneration({
    startGenerationRequest: startGeneration,
    retryGenerationRequest: (generationData) => {
      return retryGeneration({
        pageId: page.id,
        generationData: generationData,
      });
    },
    checkStatusRequest: (generationData) => {
      return checkGenerationStatus({
        pageId: page.id,
        generationData: generationData,
      });
    },
    getResultRequest: (generationData) => {
      return getGenerationResult({
        pageId: page.id,
        generationData: generationData,
      });
    },
    rateGenerationRequest: (params: {
      generationData: { id: number };
      aditionalData: { evaluation: 'positive' | 'negative' };
    }) => {
      return rateGeneration({
        pageId: page.id,
        generationData: params.generationData,
        aditionalData: params.aditionalData,
      });
    },
  });

  React.useEffect(() => {
    setCardState(() => {
      switch (aiGeneration.state) {
        case 'generating':
        case 'retrying':
          return 'loading';
        case 'completed':
          return 'completed';
        default:
          return 'idle';
      }
    });
  }, [aiGeneration.state]);

  React.useEffect(() => {
    // Close card after save page
    if (page.currentStep === 'page') {
      setIsShowAIActions(false);
      onCardClose();
    }

    // Close card after change tab
    return () => {
      onCardClose();
    };
  }, [page.currentStep]);

  const onCardGenerate = async () => {
    try {
      const response = await aiGeneration.generateAndPoll({ startParams: { pageId: page.id } });
      if (response) {
        updatePage(response);
      }
    } catch (e) {
      handleGenerationError(e);
    }
  };

  const onCardRetry = async () => {
    try {
      const response = await aiGeneration.retryAndPoll();
      if (response) {
        updatePage(response);
      }
    } catch (e) {
      handleGenerationError(e);
    }
  };

  const onCardRate = async (rate: 'positive' | 'negative') => {
    try {
      return await aiGeneration.rateGeneration.mutateAsync({ evaluation: rate });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to rate generation';
      showErrorSnackbar(errorMessage);
      console.error(error);
    }
  };

  const onCardCancelGeneration = () => {
    try {
      aiGeneration.cancelGeneration();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel generation';
      showErrorSnackbar(errorMessage);
      console.error(error);
    }
  };

  const onCardClose = () => {
    onCardCancelGeneration();
    setCardState('idle');
  };

  const onCardClear = async () => {
    const lens = R.lensPath(['pages', 0]);
    const newData = R.set(lens, { ...landingData.pages[0], ...mockData(page) }, landingData);
    await dispatch(setLandingData(newData));
    setCardState('idle');
    await dispatch(setAppHasChangesAction(false));
  };

  const updatePage = async (page: PageGenerationResult) => {
    const newLandingData = getNewLandingData(landingData, page);
    await dispatch(setLandingData(newLandingData));
    await dispatch(setAppHasChangesAction(true));
  };

  const handleGenerationError = (error: unknown) => {
    const errorMessage = (() => {
      if (!(error instanceof Error)) {
        return 'Failed to generate page';
      } else if (error.message.includes('Operation aborted')) {
        return 'The generation process has been canceled';
      } else if (error.message.includes('invalid_context')) {
        return 'Sorry, we’re unable to generate text for that request. Please try rephrasing or submitting a different request.';
      } else {
        return 'Failed to generate page';
      }
    })();

    showErrorSnackbar(errorMessage);
    console.error(error);
  };

  return (
    <PageGenerationContext.Provider
      value={{
        cardState,
        isShowAIActions,
        onCardClear,
        onCardClose,
        onCardCancelGeneration,
        onCardGenerate,
        onCardRetry,
        onCardRate,
      }}
    >
      {children}
    </PageGenerationContext.Provider>
  );
};

export const usePageGenerationContext = () => {
  const context = React.useContext(PageGenerationContext);
  if (!context) {
    throw new Error('usePageGenerationContext must be used within a PageGenerationProvider');
  }
  return context;
};
